import { ShotOutcome } from './types';

interface AudioContextLike {
  currentTime: number;
  destination: AudioNode;
  createGain: () => GainNode;
  createOscillator: () => OscillatorNode;
  resume?: () => Promise<void>;
}

type AudioContextFactory = () => AudioContextLike;

const defaultFactory: AudioContextFactory = () => {
  const AudioContextConstructor = window.AudioContext;
  return new AudioContextConstructor();
};

export const createGameAudio = (factory: AudioContextFactory = defaultFactory) => {
  let context: AudioContextLike | null = null;
  let muted = false;

  const tone = (frequency: number, durationMs: number, volume: number, delayMs = 0) => {
    if (!context || muted) return;

    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const startsAt = context.currentTime + delayMs / 1000;
    const endsAt = startsAt + durationMs / 1000;

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, startsAt);
    gain.gain.setValueAtTime(0.0001, startsAt);
    gain.gain.exponentialRampToValueAtTime(volume, startsAt + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, endsAt);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(startsAt);
    oscillator.stop(endsAt + 0.02);
  };

  return {
    activate() {
      if (!context) {
        try {
          context = factory();
        } catch {
          context = null;
        }
      }
      void context?.resume?.();
    },
    playKick() {
      tone(116, 72, 0.055);
      tone(220, 52, 0.025, 18);
    },
    playOutcome(outcome: ShotOutcome, perfect: boolean) {
      if (outcome === 'goal') {
        tone(perfect ? 660 : 520, 130, 0.04);
        tone(perfect ? 880 : 700, 180, 0.032, 84);
      } else if (outcome === 'save') {
        tone(190, 150, 0.045);
      } else {
        tone(132, 190, 0.038);
      }
    },
    setMuted(nextMuted: boolean) {
      muted = nextMuted;
    },
    isMuted() {
      return muted;
    },
    hasActivated() {
      return context !== null;
    },
  };
};

export type GameAudio = ReturnType<typeof createGameAudio>;
